import {
  AlertTriangle,
  ArrowRight,
  Database,
  Download,
  FileSpreadsheet,
  Languages,
  ListChecks,
  LoaderCircle,
  Octagon,
  Pause,
  Play,
  RefreshCw,
  Save,
  ShieldCheck,
  Square,
  Sparkles,
  Trash2,
  UploadCloud,
} from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { api, downloadFile } from "../api";
import { aiTitleOptionCsvHeader, createAiTitleOptionCsvSink, serializeAiTitleOptionCsvRow } from "../ai-title-options";
import { readAiWorkspaceState, writeAiWorkspaceState, type AiWorkspaceFilter } from "../ai-workspace-state";
import { PageHeader, Pagination, StatusBadge } from "../components";
import { useAppData } from "../context";
import type { AiAutomationResult, AiBatchDetail, AiConfig, AiContentBatch, AiContentBatchSummary, AiContentCandidate, AiContentMode, AiTitleOptionTarget, ImportJob } from "../types";

const filters: Array<{ value: AiWorkspaceFilter; label: string }> = [
  { value: "ALL", label: "全部" },
  { value: "PENDING_REVIEW", label: "待自动处理" },
  { value: "APPROVED", label: "已就绪" },
  { value: "REJECTED", label: "不采用" },
  { value: "GENERATION_FAILED", label: "生成失败" },
  { value: "SUBMITTED", label: "已上传" },
];

const modeLabels = { TITLE: "标题", DESCRIPTION: "描述", BOTH: "标题 + 描述" } as const;
const sourceLookupLabels = {
  FILE_PROVIDED: "文件资料",
  YIMAI_MATCHED: "亿迈已匹配",
  YIMAI_NOT_FOUND: "亿迈无记录",
  YIMAI_LOOKUP_FAILED: "亿迈查询失败",
} as const;
const titleSourceLabels = {
  YIMAI_TITLE_EN: "亿迈英文标题",
  YIMAI_ITEM_TITLE: "亿迈商品标题",
  YIMAI_TITLE_CN: "亿迈中文标题",
  FILE_TITLE: "上传文件标题",
  MISSING: "缺少标题",
} as const;
const descriptionSourceLabels = {
  YIMAI_PRODUCT_DATA: "亿迈商品资料",
  YIMAI_AND_FILE: "亿迈 + 文件补充",
  MERCADO_LIBRE_DESCRIPTION: "历史美客多资料（禁止上传）",
  FILE_DESCRIPTION: "上传文件描述",
  MISSING: "缺少描述",
} as const;
const stageLabels = {
  COLLECTING: "采集中",
  SOURCE_READY: "资料已就绪",
  GENERATING: "AI 处理中",
  REVIEWING: "可自动上传",
  PUBLISHING: "自动上传中",
  AUTOMATED: "自动处理完成",
  SUBMITTED: "已全部提交",
} as const;
const controlLabels = {
  ACTIVE: null,
  PAUSE_REQUESTED: "暂停中",
  PAUSED: "已暂停",
  STOP_REQUESTED: "停止中",
  STOPPED: "已停止",
} as const;

function batchStateLabel(batch: AiContentBatch) {
  return controlLabels[batch.controlState] ?? stageLabels[batch.stage];
}

function batchStateClass(batch: AiContentBatch) {
  return batch.controlState === "ACTIVE" ? batch.stage.toLowerCase() : batch.controlState.toLowerCase();
}

interface TitleOptionProgress {
  kind: "download";
  status: "running" | "completed" | "cancelled" | "failed";
  total: number;
  completed: number;
  failed: number;
  message: string;
  direct: boolean;
}

type AiWorkbenchMode = "BATCH" | "SINGLE";

function charCount(value: string) {
  return Array.from(value).length;
}

function formatTime(value: string) {
  return new Intl.DateTimeFormat("zh-CN", { month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit", hour12: false }).format(new Date(value));
}

function batchProgressText(batch: AiContentBatch) {
  if (batch.stage === "COLLECTING") return `${batch.totalCount.toLocaleString()} 条 · 正在采集商品资料`;
  if (batch.stage === "GENERATING") return `${batch.totalCount.toLocaleString()} 条 · AI 正在处理`;
  if (batch.stage === "PUBLISHING") return `${batch.totalCount.toLocaleString()} 条 · 正在自动校验并上传`;
  return `${batch.totalCount.toLocaleString()} 条 · 待处理 ${batch.pendingCount.toLocaleString()} · 需修正 ${batch.failedCount.toLocaleString()} · 已上传 ${batch.submittedCount.toLocaleString()}`;
}

export function AiContent() {
  const { data, refresh: refreshApp } = useAppData();
  const [initialWorkspace] = useState(() => readAiWorkspaceState(window.localStorage));
  const [config, setConfig] = useState<AiConfig | null>(null);
  const [batches, setBatches] = useState<AiContentBatchSummary[]>([]);
  const [batchId, setBatchId] = useState(initialWorkspace.batchId);
  const [detail, setDetail] = useState<AiBatchDetail | null>(null);
  const [automationResult, setAutomationResult] = useState<AiAutomationResult | null>(null);
  const [sourceFile, setSourceFile] = useState<File | null>(null);
  const [listingFile, setListingFile] = useState<File | null>(null);
  const [workbenchMode, setWorkbenchMode] = useState<AiWorkbenchMode>("BATCH");
  const [uploadCollapsed, setUploadCollapsed] = useState(Boolean(initialWorkspace.batchId));
  const [accountCode, setAccountCode] = useState("");
  const [contentMode, setContentMode] = useState<AiContentMode>("BOTH");
  const [filter, setFilter] = useState<AiWorkspaceFilter>(initialWorkspace.filter);
  const [page, setPage] = useState(initialWorkspace.page);
  const [selectedId, setSelectedId] = useState(initialWorkspace.selectedId);
  const [selected, setSelected] = useState<AiContentCandidate | null>(null);
  const [selectedLoading, setSelectedLoading] = useState(false);
  const [reviewTitle, setReviewTitle] = useState("");
  const [reviewDescription, setReviewDescription] = useState("");
  const [busy, setBusy] = useState("");
  const [error, setError] = useState("");
  const [restoring, setRestoring] = useState(true);
  const [showAllBatches, setShowAllBatches] = useState(false);
  const [showBatchRecords, setShowBatchRecords] = useState(true);
  const [titleOptionProgress, setTitleOptionProgress] = useState<TitleOptionProgress | null>(null);
  const sourceFileRef = useRef<HTMLInputElement>(null);
  const listingFileRef = useRef<HTMLInputElement>(null);
  const reviewRef = useRef<HTMLElement>(null);
  const titleOptionAbortRef = useRef<AbortController | null>(null);
  const resumeTargetRef = useRef("");
  const batchRequestRef = useRef(0);
  const pageSize = 50;

  async function loadBatches(preferredId?: string) {
    const response = await api.aiBatches();
    setBatches(response.items);
    setBatchId((current) => {
      if (response.items.some((batch) => batch.id === current)) return current;
      if (preferredId && response.items.some((batch) => batch.id === preferredId)) return preferredId;
      return response.items[0]?.id || "";
    });
  }

  async function loadBatch(id: string, targetPage = page, targetFilter = filter, signal?: AbortSignal) {
    const requestId = ++batchRequestRef.current;
    const query = new URLSearchParams({ page: String(targetPage), pageSize: String(pageSize), status: targetFilter });
    const response = await api.aiBatch(id, query, signal);
    if (signal?.aborted || requestId !== batchRequestRef.current) return;
    let currentJob: ImportJob<AiContentBatch | AiAutomationResult> | null = null;
    if (response.batch.importJobId) currentJob = await api.importJob<AiContentBatch | AiAutomationResult>(response.batch.importJobId);
    if (signal?.aborted || requestId !== batchRequestRef.current) return;
    setDetail(response);
    if (currentJob?.kind === "AI_CONTENT_PUBLISH" && currentJob.result) {
      setAutomationResult(currentJob.result as AiAutomationResult);
    } else if (currentJob?.kind !== "AI_CONTENT_PUBLISH") {
      setAutomationResult(null);
    }
  }

  useEffect(() => {
    let stopped = false;
    void Promise.allSettled([api.aiConfig(), api.aiBatches()]).then(([configResult, batchesResult]) => {
      if (stopped) return;
      if (configResult.status === "fulfilled") setConfig(configResult.value);
      if (batchesResult.status === "fulfilled") {
        const response = batchesResult.value;
        setBatches(response.items);
        const storedBatchExists = response.items.some((batch) => batch.id === initialWorkspace.batchId);
        const nextId = storedBatchExists ? initialWorkspace.batchId : response.items[0]?.id || "";
        setBatchId(nextId);
        if (!storedBatchExists) {
          setPage(1);
          setFilter("ALL");
          setSelectedId("");
        }
      } else {
        setError(batchesResult.reason instanceof Error ? batchesResult.reason.message : "历史任务读取失败，请刷新重试。");
      }
      setRestoring(false);
    });
    return () => { stopped = true; };
  }, [initialWorkspace]);

  useEffect(() => {
    if (accountCode || !data?.accounts.length) return;
    setAccountCode(data.accounts.find((account) => account.status === "ACTIVE")?.accountCode || data.accounts[0].accountCode);
  }, [accountCode, data?.accounts]);

  useEffect(() => {
    if (restoring) return;
    if (!batchId) { setDetail(null); return; }
    const controller = new AbortController();
    setError("");
    void loadBatch(batchId, page, filter, controller.signal).catch((cause) => {
      if (cause instanceof DOMException && cause.name === "AbortError") return;
      setError(cause instanceof Error ? cause.message : "候选读取失败。");
    });
    return () => controller.abort();
  }, [batchId, filter, page, restoring]);

  useEffect(() => {
    if (restoring) return;
    writeAiWorkspaceState(window.localStorage, { batchId, page, filter, selectedId });
  }, [batchId, filter, page, restoring, selectedId]);

  const selectedBatchIsActive = batches.some((batch) => batch.id === batchId && batch.progress.active);
  useEffect(() => {
    if (!batchId || !detail || detail.batch.controlState === "PAUSED" || detail.batch.controlState === "STOPPED"
      || (!selectedBatchIsActive && !["COLLECTING", "GENERATING", "PUBLISHING"].includes(detail.batch.stage))) return;
    const timer = window.setInterval(() => {
      void Promise.all([loadBatch(batchId, page, filter), refreshApp()]).catch((cause) => setError(cause instanceof Error ? cause.message : "后台进度读取失败。"));
    }, 1200);
    return () => window.clearInterval(timer);
  }, [batchId, detail?.batch.controlState, detail?.batch.stage, filter, page, selectedBatchIsActive]);

  const hasActiveBatch = batches.some((batch) => batch.progress.active);
  useEffect(() => {
    if (!hasActiveBatch) return;
    const timer = window.setInterval(() => {
      void loadBatches(batchId).catch((cause) => setError(cause instanceof Error ? cause.message : "历史批次进度读取失败。"));
    }, 1500);
    return () => window.clearInterval(timer);
  }, [batchId, hasActiveBatch]);

  useEffect(() => {
    if (!detail?.items.length) { setSelectedId(""); return; }
    if (selectedId && !detail.items.some((candidate) => candidate.id === selectedId)) setSelectedId("");
  }, [detail?.items, selectedId]);

  useEffect(() => {
    if (!selectedId || !detail || !["REVIEWING", "AUTOMATED", "SUBMITTED"].includes(detail.batch.stage)) {
      setSelected(null);
      setSelectedLoading(false);
      return;
    }
    const controller = new AbortController();
    setSelected(null);
    setSelectedLoading(true);
    void api.aiCandidate(selectedId, controller.signal).then((candidate) => {
      if (!controller.signal.aborted) setSelected(candidate);
    }).catch((cause) => {
      if (cause instanceof DOMException && cause.name === "AbortError") return;
      setError(cause instanceof Error ? cause.message : "商品详情读取失败。");
    }).finally(() => {
      if (!controller.signal.aborted) setSelectedLoading(false);
    });
    return () => controller.abort();
  }, [detail?.batch.stage, detail?.batch.updatedAt, selectedId]);

  useEffect(() => {
    if (!detail || resumeTargetRef.current !== detail.batch.id) return;
    resumeTargetRef.current = "";
    window.requestAnimationFrame(() => reviewRef.current?.scrollIntoView({ behavior: "auto", block: "start" }));
  }, [detail?.batch.id]);

  useEffect(() => {
    if (!selected) return;
    setReviewTitle(selected.reviewTitle);
    setReviewDescription(selected.reviewDescription);
  }, [selected?.id, selected?.updatedAt]);

  function continueBatch(batch: AiContentBatch) {
    const alreadyOpen = batch.id === batchId && detail?.batch.id === batch.id;
    setAutomationResult(null);
    setWorkbenchMode("BATCH");
    setShowBatchRecords(true);
    if (batch.id !== batchId) {
      setPage(1);
      setFilter("ALL");
      setSelectedId("");
    }
    setBatchId(batch.id);
    if (alreadyOpen) {
      window.requestAnimationFrame(() => reviewRef.current?.scrollIntoView({ behavior: "auto", block: "start" }));
    } else {
      resumeTargetRef.current = batch.id;
    }
  }

  async function controlBatch(batch: AiContentBatch, action: "pause" | "resume" | "stop" | "delete") {
    if (action === "pause" && !window.confirm("暂停后会等待当前最多 3 条处理完成，并保留全部结果。确认暂停？")) return;
    if (action === "stop" && !window.confirm("停止后不可继续，但已处理结果仍可导出。确认停止该批次？")) return;
    if (action === "delete") {
      if (!window.confirm("这会永久删除批次、候选结果和该批次独占的源文件，且不会撤销已写入 Mercado Libre 的内容。确认继续？")) return;
      if (!window.confirm(`再次确认永久删除“${batch.originalName}”？`)) return;
    }
    const busyKey = `${action}:${batch.id}`;
    setBusy(busyKey); setError("");
    try {
      if (action === "pause") await api.pauseAiBatch(batch.id);
      else if (action === "resume") await api.resumeAiBatch(batch.id);
      else if (action === "stop") await api.stopAiBatch(batch.id);
      else await api.deleteAiBatch(batch.id);
      if (action === "delete") {
        if (batch.id === batchId) {
          setDetail(null); setSelected(null); setSelectedId("");
        }
        await loadBatches();
      } else {
        await Promise.all([loadBatches(batch.id), loadBatch(batch.id, page, filter)]);
      }
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "批次操作失败。");
    } finally { setBusy(""); }
  }

  async function upload(autoPublish = false) {
    if (!sourceFile) return setError("请选择商品资料 CSV 或 XLSX 文件。");
    if (!accountCode) return setError("请选择已授权的 Mercado Libre 店铺。");
    const busyKey = autoPublish ? "upload-auto" : "upload";
    setBusy(busyKey); setError(""); setAutomationResult(null);
    try {
      const response = await api.createAiBatch(sourceFile, {
        accountCode,
        contentMode,
        listingFile,
        autoPublish,
        automationConcurrency: 3,
      });
      setUploadCollapsed(true);
      setShowBatchRecords(true);
      setBatchId(response.batch.id);
      setPage(1); setFilter("ALL");
      await Promise.all([loadBatches(response.batch.id), loadBatch(response.batch.id, 1, "ALL")]);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : autoPublish ? "全自动任务创建失败。" : "AI 生成批次创建失败。");
    } finally { setBusy(""); }
  }

  function resetUploadFiles() {
    setSourceFile(null);
    setListingFile(null);
    if (sourceFileRef.current) sourceFileRef.current.value = "";
    if (listingFileRef.current) listingFileRef.current.value = "";
    setUploadCollapsed(false);
  }

  function openCandidate(candidateId: string) {
    setSelectedId(candidateId);
    setWorkbenchMode("SINGLE");
  }

  async function startGeneration() {
    if (!detail) return;
    setBusy("generate"); setError("");
    try {
      const response = await api.generateAiBatch(detail.batch.id);
      await Promise.all([loadBatch(detail.batch.id, page, filter), loadBatches(detail.batch.id)]);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "AI 处理启动失败。");
    } finally { setBusy(""); }
  }

  async function updateCandidate() {
    if (!selected) return;
    setBusy(`candidate:${selected.id}`); setError("");
    try {
      await api.updateAiCandidate(selected.id, { title: reviewTitle, description: reviewDescription, status: "PENDING_REVIEW" });
      await Promise.all([loadBatch(selected.batchId, page, filter), loadBatches(selected.batchId)]);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "内容保存失败。");
    } finally { setBusy(""); }
  }

  async function regenerate() {
    if (!selected) return;
    setBusy(`regenerate:${selected.id}`); setError("");
    try {
      await api.regenerateAiCandidate(selected.id);
      await Promise.all([loadBatch(selected.batchId, page, filter), loadBatches(selected.batchId)]);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "重新生成失败。");
    } finally { setBusy(""); }
  }

  async function automateBatch() {
    if (!detail) return;
    const pending = Math.max(0, detail.batch.totalCount - detail.batch.submittedCount);
    if (!window.confirm(
      `将对 ${pending.toLocaleString()} 条尚未上传的商品进行自动提交。\n\n只要商品映射正确且存在非空标题或描述，就会建立正式任务并交由 Mercado Libre 接口判断；单条接口拒绝不会停止批次。确认继续？`,
    )) return;
    setBusy("automate"); setError(""); setAutomationResult(null);
    try {
      const response = await api.automateAiBatch(detail.batch.id);
      await Promise.all([loadBatch(detail.batch.id, page, filter), loadBatches(detail.batch.id), refreshApp()]);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "自动上传启动失败。");
    } finally { setBusy(""); }
  }

  async function retryFixableFailures() {
    if (!detail || !detail.retryableFailures.candidateCount) return;
    const retry = detail.retryableFailures;
    if (!window.confirm(
      `将重新生成 ${retry.candidateCount.toLocaleString()} 条可修复 Listing（${retry.skuCount.toLocaleString()} 个 SKU）。\n\n生成后只要存在非空标题或描述就会提交 Mercado Libre；目录商品标题仍跳过，目录商品描述允许提交，单条接口拒绝不会停止批次。确认继续？`,
    )) return;
    setBusy("retry-fixable"); setError(""); setAutomationResult(null);
    try {
      const response = await api.retryFixableAiBatch(detail.batch.id);
      await Promise.all([loadBatch(detail.batch.id, page, filter), loadBatches(detail.batch.id), refreshApp()]);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "可修复项重新生成失败。");
    } finally { setBusy(""); }
  }

  async function downloadIncomplete() {
    if (!detail) return;
    setBusy("download-incomplete"); setError("");
    try {
      await downloadFile(
        `/api/ai/content/batches/${encodeURIComponent(detail.batch.id)}/incomplete-export`,
        `meliforge-ai-incomplete-${detail.batch.id}.xlsx`,
      );
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "待修正 SKU 下载失败。");
    } finally { setBusy(""); }
  }

  async function downloadBatchResult(batch: AiContentBatch) {
    const busyKey = `export:${batch.id}`;
    setBusy(busyKey); setError("");
    try {
      await downloadFile(
        `/api/ai/content/batches/${encodeURIComponent(batch.id)}/export`,
        `meliforge-ai-all-data-${batch.id}.xlsx`,
      );
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "改写结果下载失败。");
    } finally { setBusy(""); }
  }

  async function downloadEnglishTitleOptions() {
    if (!detail) return;
    const controller = new AbortController();
    titleOptionAbortRef.current = controller;
    setBusy("title-options-download");
    setError("");
    let sink: Awaited<ReturnType<typeof createAiTitleOptionCsvSink>> | null = null;
    let completed = 0;
    let failed = 0;
    let total = 0;
    try {
      const stamp = new Date().toISOString().replace(/[-:]/g, "").replace(/\.\d{3}Z$/, "Z");
      sink = await createAiTitleOptionCsvSink(`meliforge-english-title-options-${detail.batch.id}-${stamp}.csv`);
      await sink.write(aiTitleOptionCsvHeader());
      setTitleOptionProgress({ kind: "download", status: "running", total: 0, completed: 0, failed: 0, message: sink.direct ? "正在直接写入本机文件" : "浏览器将在完成后下载文件", direct: sink.direct });
      const pageSize = 60;
      let targetPage = 1;
      while (true) {
        const response = await api.aiTitleOptionTargets(detail.batch.id, targetPage, pageSize, controller.signal);
        total = response.total;
        if (!response.items.length) break;
        for (let start = 0; start < response.items.length; start += 3) {
          if (controller.signal.aborted) throw new DOMException("Aborted", "AbortError");
          const targets = response.items.slice(start, start + 3);
          const results = await Promise.all(targets.map(async (target: AiTitleOptionTarget) => {
            if (!target.eligible) return { target, error: target.eligibilityErrors.join("；") };
            try {
              const result = await api.generateAiTitleOptions(detail.batch.id, target, controller.signal);
              return { target, titles: result.titles };
            } catch (cause) {
              if (cause instanceof DOMException && cause.name === "AbortError") throw cause;
              return { target, error: cause instanceof Error ? cause.message : "英文标题候选生成失败。" };
            }
          }));
          for (const result of results) {
            if (result.error) failed += 1;
            await sink.write(serializeAiTitleOptionCsvRow({
              target: result.target,
              titles: result.titles,
            }));
          }
          completed += results.length;
          setTitleOptionProgress({ kind: "download", status: "running", total, completed, failed, message: sink.direct ? "候选生成后立即写入本机" : "候选仅暂存在本次下载内存中", direct: sink.direct });
        }
        if (completed >= total) break;
        targetPage += 1;
      }
      await sink.finish();
      setTitleOptionProgress({ kind: "download", status: "completed", total, completed, failed, message: failed ? "下载完成；生成失败商品的新标题列已留空" : "英文标题候选已保存到本机", direct: sink.direct });
    } catch (cause) {
      const cancelled = cause instanceof DOMException && cause.name === "AbortError";
      if (sink) await sink.closePartial().catch(() => undefined);
      if (cancelled && !sink) {
        setTitleOptionProgress(null);
      } else {
        setTitleOptionProgress({
          kind: "download",
          status: cancelled ? "cancelled" : "failed",
          total,
          completed,
          failed,
          message: cancelled ? (sink?.direct ? "已停止，已完成部分保留在本机文件中" : "已停止，未生成下载文件") : cause instanceof Error ? cause.message : "英文标题候选下载失败。",
          direct: sink?.direct ?? false,
        });
      }
    } finally {
      titleOptionAbortRef.current = null;
      setBusy("");
    }
  }

  const titleCount = charCount(reviewTitle);
  const descriptionCount = charCount(reviewDescription);
  const titleRequired = selected?.contentMode === "TITLE" || selected?.contentMode === "BOTH";
  const descriptionRequired = selected?.contentMode === "DESCRIPTION" || selected?.contentMode === "BOTH";
  const reviewBlocked = Boolean(selected && (
    (titleRequired && Boolean(reviewTitle.trim()) && titleCount > selected.titleMaxChars)
    || (descriptionRequired && Boolean(reviewDescription.trim()) && descriptionCount > selected.descriptionMaxChars)
  ));
  const resumeBatch = batches.find((batch) => batch.id === batchId) ?? batches[0] ?? null;
  const focusBatch = batches.find((batch) => batch.progress.active) ?? resumeBatch;
  const visibleBatches = showAllBatches ? batches : batches.slice(0, 5);
  const generatedCount = focusBatch
    ? focusBatch.stage === "COLLECTING" || focusBatch.stage === "SOURCE_READY" ? 0
      : focusBatch.stage === "GENERATING"
        ? Math.min(focusBatch.totalCount - focusBatch.failedCount, Math.max(0, Math.round(focusBatch.totalCount * Math.max(0, (focusBatch.progress.jobProgress - 15) / 80))))
        : Math.max(0, focusBatch.totalCount - focusBatch.failedCount)
    : 0;
  const focusWrite = focusBatch?.progress.writeResult;
  const focusExceptions = focusBatch ? focusBatch.failedCount + (focusWrite?.failed ?? 0) + (focusWrite?.review ?? 0) : 0;
  const matchSummary = detail?.batch.matchSummary;
  const titleOptionRunning = titleOptionProgress?.status === "running";
  const titleOptionPercent = titleOptionProgress?.status === "completed" && titleOptionProgress.total === 0
    ? 100
    : titleOptionProgress?.total
      ? Math.min(100, Math.round((titleOptionProgress.completed / titleOptionProgress.total) * 100))
      : 0;
  const uploadedBatch = detail?.batch ?? focusBatch;

  return <div className="ai-content-page">
    <PageHeader eyebrow="CONTENT WORKBENCH" title="AI 内容改写" actions={<div className="ai-header-actions">
      <span className={config?.configured ? "ai-service-chip ready" : "ai-service-chip error"}>{config?.configured ? "AI 已连接" : "AI 未连接"}</span>
      <button className="button secondary ai-template-button" onClick={() => void downloadFile("/api/ai/content/template", "meliforge-ai-content-template.xlsx")}><Download size={16} />模板</button>
    </div>} />

    <div className="ai-workbench-tabs" role="tablist" aria-label="改写模式">
      <button type="button" role="tab" aria-selected={workbenchMode === "BATCH"} className={workbenchMode === "BATCH" ? "active" : ""} onClick={() => setWorkbenchMode("BATCH")}>批量改写</button>
      <button type="button" role="tab" aria-selected={workbenchMode === "SINGLE"} className={workbenchMode === "SINGLE" ? "active" : ""} onClick={() => setWorkbenchMode("SINGLE")}>单条改写</button>
    </div>

    {error ? <div className="form-error ai-page-error" role="alert"><AlertTriangle size={17} />{error}</div> : null}

    {workbenchMode === "BATCH" ? <div className="ai-batch-mode" role="tabpanel">
      <section className={`ai-control-surface ${uploadCollapsed && uploadedBatch ? "is-collapsed" : ""}`}>
        {uploadCollapsed && uploadedBatch ? <div className="ai-file-summary">
          <div className="ai-file-summary-main"><FileSpreadsheet size={18} /><span><strong title={uploadedBatch.originalName}>{uploadedBatch.originalName}</strong><small>已读取 {uploadedBatch.totalCount.toLocaleString()} 条{uploadedBatch.listingName ? ` · Listing：${uploadedBatch.listingName}` : ""}</small></span></div>
          <div className="ai-file-summary-actions"><button type="button" className="text-button" onClick={() => setUploadCollapsed(false)}>重新上传</button><button type="button" className="text-button danger" onClick={resetUploadFiles}>清空</button></div>
        </div> : <div className="ai-upload-area">
          <div className="ai-control-title"><h2>导入文件</h2><span>CSV / XLSX</span></div>
          <div className="ai-upload-grid">
            <div className={`ai-compact-file ${sourceFile ? "has-file" : ""}`} onDragOver={(event) => event.preventDefault()} onDrop={(event) => { event.preventDefault(); setSourceFile(event.dataTransfer.files[0] ?? null); }}>
              <input ref={sourceFileRef} type="file" tabIndex={-1} aria-hidden="true" accept=".csv,text/csv,.xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" onChange={(event) => setSourceFile(event.target.files?.[0] ?? null)} />
              <FileSpreadsheet size={19} /><span><small>商品资料</small><strong title={sourceFile?.name}>{sourceFile?.name || "选择 CSV / XLSX"}</strong></span><button type="button" className="button secondary" onClick={() => sourceFileRef.current?.click()}>{sourceFile ? "更换" : "选择"}</button>
            </div>
            <div className={`ai-compact-file ${listingFile ? "has-file" : ""}`} onDragOver={(event) => event.preventDefault()} onDrop={(event) => { event.preventDefault(); setListingFile(event.dataTransfer.files[0] ?? null); }}>
              <input ref={listingFileRef} type="file" tabIndex={-1} aria-hidden="true" accept=".csv,text/csv,.xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" onChange={(event) => setListingFile(event.target.files?.[0] ?? null)} />
              <ListChecks size={19} /><span><small>在线 Listing（可选）</small><strong title={listingFile?.name}>{listingFile?.name || "使用已保存商品映射"}</strong></span><button type="button" className="button secondary" onClick={() => listingFileRef.current?.click()}>{listingFile ? "更换" : "选择"}</button>
            </div>
          </div>
        </div>}

        <div className="ai-settings-bar">
          <div className="field-group ai-account-setting"><label htmlFor="ai-account-code">店铺</label><select id="ai-account-code" value={accountCode} onChange={(event) => setAccountCode(event.target.value)}>{data?.accounts.map((account) => <option key={account.id} value={account.accountCode}>{account.accountCode} · {account.sites.join("/")}</option>)}</select></div>
          <div className="field-group ai-mode-setting"><label>改写内容</label><div className="segmented ai-content-mode">{(["BOTH", "TITLE", "DESCRIPTION"] as AiContentMode[]).map((mode) => <button type="button" key={mode} className={contentMode === mode ? "active" : ""} onClick={() => setContentMode(mode)}>{modeLabels[mode]}</button>)}</div></div>
          <div className="ai-setting-actions"><span className="ai-execution-note"><ShieldCheck size={15} />并发 3</span><button className="button secondary" disabled={!sourceFile || !accountCode || busy.startsWith("upload")} onClick={() => void upload(false)}>{busy === "upload" ? <LoaderCircle className="spin" size={16} /> : <Database size={16} />}只生成</button><button className="button primary ai-primary-command" disabled={!sourceFile || !accountCode || !data?.writeEnabled || !config?.configured || busy.startsWith("upload")} onClick={() => void upload(true)}>{busy === "upload-auto" ? <LoaderCircle className="spin" size={17} /> : <UploadCloud size={17} />}改写并上传到美客多</button></div>
        </div>
      </section>

      {restoring ? <section className="ai-live-strip loading" role="status"><LoaderCircle className="spin" size={17} /><span>正在读取批次…</span></section> : focusBatch ? <section className="ai-live-strip">
        <div className="ai-live-heading"><span className={`ai-stage-label ${batchStateClass(focusBatch)}`}>{focusBatch.progress.label}</span><strong title={focusBatch.originalName}>{focusBatch.originalName}</strong><small>{focusBatch.progress.detail}</small><b>{focusBatch.progress.percent}%</b></div>
        <progress className={`ai-master-progress ${focusBatch.progress.phase}`} max="100" value={focusBatch.progress.percent} />
        <div className="ai-live-footer"><div className="ai-live-metrics"><span>总数 <b>{focusBatch.totalCount.toLocaleString()}</b></span><span>已生成 <b>{generatedCount.toLocaleString()}</b></span><span>已上传 <b>{(focusWrite?.success ?? 0).toLocaleString()}</b></span><span>跳过 <b>{(focusWrite?.skipped ?? focusBatch.rejectedCount).toLocaleString()}</b></span><span className={focusExceptions ? "risk" : ""}>异常 <b>{focusExceptions.toLocaleString()}</b></span></div><div className="ai-live-actions">
          <button className="icon-button" disabled={!focusBatch.totalCount || busy === `export:${focusBatch.id}`} title="下载结果" aria-label="下载结果" onClick={() => void downloadBatchResult(focusBatch)}>{busy === `export:${focusBatch.id}` ? <LoaderCircle className="spin" size={14} /> : <Download size={14} />}</button>
          {focusBatch.controlState === "ACTIVE" && focusBatch.stage === "GENERATING" ? <button className="icon-button" disabled={busy.endsWith(`:${focusBatch.id}`)} title="暂停批次" aria-label="暂停批次" onClick={() => void controlBatch(focusBatch, "pause")}><Pause size={14} /></button> : null}
          {focusBatch.controlState === "PAUSED" ? <button className="icon-button" disabled={busy.endsWith(`:${focusBatch.id}`)} title="继续批次" aria-label="继续批次" onClick={() => void controlBatch(focusBatch, "resume")}><Play size={14} /></button> : null}
          {["ACTIVE", "PAUSE_REQUESTED", "PAUSED"].includes(focusBatch.controlState) && !["COLLECTING", "PUBLISHING"].includes(focusBatch.stage) && focusBatch.taskIds.length === 0 ? <button className="icon-button danger" disabled={busy.endsWith(`:${focusBatch.id}`)} title="停止批次" aria-label="停止批次" onClick={() => void controlBatch(focusBatch, "stop")}><Octagon size={14} /></button> : null}
        </div></div>
      </section> : null}

      <div className="ai-data-layout">
        {batches.length ? <aside className="ai-history-panel">
          <div className="ai-panel-heading"><div><h2>最近批次</h2><span>{batches.length}</span></div>{batches.length > 5 ? <button className="text-button" onClick={() => setShowAllBatches((current) => !current)}>{showAllBatches ? "收起" : "全部"}</button> : null}</div>
          <div className="ai-history-list">{visibleBatches.map((batch) => {
            const actionBusy = busy.endsWith(`:${batch.id}`);
            const canDelete = !batch.taskIds.length && (["PAUSED", "STOPPED"].includes(batch.controlState) || (!["COLLECTING", "GENERATING", "PUBLISHING"].includes(batch.stage) && batch.controlState === "ACTIVE"));
            return <div key={batch.id} className={batch.id === batchId ? "ai-history-row selected" : "ai-history-row"}>
              <button className="ai-history-main" onClick={() => continueBatch(batch)}><span><strong title={batch.originalName}>{batch.originalName}</strong><small>{formatTime(batch.updatedAt)} · {batch.totalCount.toLocaleString()} 条</small></span><span className={`ai-stage-label ${batchStateClass(batch)}`}>{batch.progress.label}</span><b>{batch.progress.percent}%</b></button>
              <div className="ai-history-actions"><button className="icon-button" disabled={!batch.totalCount || busy === `export:${batch.id}`} title="下载结果" aria-label="下载结果" onClick={() => void downloadBatchResult(batch)}>{busy === `export:${batch.id}` ? <LoaderCircle className="spin" size={14} /> : <Download size={14} />}</button>{canDelete ? <button className="icon-button danger" disabled={actionBusy} title="删除批次" aria-label="删除批次" onClick={() => void controlBatch(batch, "delete")}><Trash2 size={14} /></button> : null}<button className="icon-button" title="打开批次" aria-label="打开批次" onClick={() => continueBatch(batch)}><ArrowRight size={14} /></button></div>
            </div>;
          })}</div>
        </aside> : null}

        {detail ? <section ref={reviewRef} className="ai-result-panel">
          <div className="ai-panel-heading ai-result-heading"><div><h2>批次数据 / 结果</h2><span title={detail.batch.originalName}>{detail.batch.originalName}</span></div><span className={`ai-stage-label ${batchStateClass(detail.batch)}`}>{batchStateLabel(detail.batch)}</span></div>
          <div className="ai-result-summary"><div><span>商品</span><strong>{detail.batch.totalCount.toLocaleString()}</strong></div><div><span>标题目标</span><strong>{detail.preview.titleUpdateCount.toLocaleString()}</strong></div><div><span>描述目标</span><strong>{detail.preview.descriptionUpdateCount.toLocaleString()}</strong></div><div><span>已提交</span><strong>{detail.batch.submittedCount.toLocaleString()}</strong></div><div className={detail.batch.failedCount ? "risk" : ""}><span>生成异常</span><strong>{detail.batch.failedCount.toLocaleString()}</strong></div></div>
          <div className="ai-result-actions"><button className="button secondary" onClick={() => void downloadBatchResult(detail.batch)}><Download size={15} />下载全部数据</button><button className="button secondary" onClick={() => { setFilter("GENERATION_FAILED"); setPage(1); setShowBatchRecords(true); }}><AlertTriangle size={15} />查看异常</button><button className="button secondary" onClick={() => { setFilter("ALL"); setPage(1); setShowBatchRecords((current) => !current); }}><ListChecks size={15} />{showBatchRecords ? "收起记录" : "查看记录"}</button><button className="button primary" disabled={!['REVIEWING', 'AUTOMATED', 'PUBLISHING'].includes(detail.batch.stage) || !data?.writeEnabled || detail.batch.submittedCount === detail.batch.totalCount || Boolean(busy)} onClick={() => void automateBatch()}>{busy === "automate" ? <LoaderCircle className="spin" size={16} /> : <UploadCloud size={16} />}{busy === "automate" ? "正在上传" : "上传尚未写入项"}</button>{detail.batch.stage === "SOURCE_READY" ? <button className="button primary" disabled={!config?.configured || busy === "generate"} onClick={() => void startGeneration()}>{busy === "generate" ? <LoaderCircle className="spin" size={16} /> : <Play size={16} />}继续 AI 处理</button> : null}</div>

          {showBatchRecords ? <div className="ai-records-panel">
            <div className="result-toolbar ai-review-toolbar"><div className="segmented compact">{filters.map((entry) => <button key={entry.value} className={filter === entry.value ? "active" : ""} onClick={() => { setFilter(entry.value); setPage(1); }}>{entry.label}</button>)}</div><span className="filter-count">{detail.total.toLocaleString()} 条</span></div>
            <div className="table-scroll"><table className="data-table ai-candidate-table"><thead><tr><th>行</th><th>商品</th><th>站点 / 语言</th><th>资料来源</th><th>内容</th><th>标题字符</th><th>描述字符</th><th>检查</th><th>状态</th></tr></thead><tbody>{detail.items.map((candidate) => <tr key={candidate.id} className={selectedId === candidate.id ? "selected" : ""} role="button" tabIndex={0} onClick={() => openCandidate(candidate.id)} onKeyDown={(event) => { if (event.key === "Enter" || event.key === " ") { event.preventDefault(); openCandidate(candidate.id); } }}><td className="mono">{candidate.rowNo}</td><td><strong>{candidate.merchantSku || candidate.itemId}</strong><small>{candidate.accountCode}</small></td><td><strong>{candidate.siteCode}</strong><small>{candidate.language}</small></td><td><span className={`ai-source-state ${candidate.sourceLookupStatus.toLowerCase()}`}>{sourceLookupLabels[candidate.sourceLookupStatus]}</span><small>{descriptionSourceLabels[candidate.sourceDescriptionOrigin]}</small></td><td>{modeLabels[candidate.contentMode]}</td><td className="mono">{candidate.reviewTitleLength} / {candidate.titleMaxChars}</td><td className="mono">{candidate.reviewDescriptionLength.toLocaleString()} / {candidate.descriptionMaxChars.toLocaleString()}</td><td><span className={`ai-quality-label ${candidate.aiQualityStatus.toLowerCase()}`}>{candidate.aiQualityStatus === "PASS" ? "通过" : "有备注"}</span></td><td><StatusBadge status={candidate.status} />{candidate.errorMessage ? <small className="ai-row-error">{candidate.errorMessage}</small> : null}</td></tr>)}</tbody></table></div>
            <Pagination page={detail.page} total={detail.total} pageSize={detail.pageSize} onChange={setPage} />
          </div> : null}

          <details className="ai-secondary-tools"><summary>更多数据与工具</summary>{matchSummary ? <div className="ai-compact-match"><ListChecks size={17} /><span>来源 SKU {matchSummary.matchedSourceSkuCount.toLocaleString()} / {matchSummary.sourceSkuCount.toLocaleString()}</span><span>Listing {matchSummary.matchedListingCount.toLocaleString()} / {matchSummary.listingRowCount.toLocaleString()}</span><span className={matchSummary.blockedTargetCount ? "risk" : ""}>隔离 {matchSummary.blockedTargetCount.toLocaleString()}</span></div> : null}<div className="ai-coverage-grid compact"><div><span>Item Title</span><strong>{detail.coverage.originalTitleCount}</strong></div><div><span>Features</span><strong>{detail.coverage.featureCount}</strong></div><div><span>Specification</span><strong>{detail.coverage.specCount}</strong></div><div><span>Package List</span><strong>{detail.coverage.packageListCount}</strong></div><div><span>西语完成</span><strong>{detail.coverage.spanishCompletedCount}</strong></div><div><span>葡语完成</span><strong>{detail.coverage.portugueseCompletedCount}</strong></div></div><div className="ai-tool-actions"><button className="button secondary" disabled={!config?.configured || !["REVIEWING", "AUTOMATED"].includes(detail.batch.stage) || Boolean(busy)} onClick={() => void downloadEnglishTitleOptions()}><Languages size={16} />生成并下载 10 个英文标题</button>{titleOptionRunning ? <button className="icon-button" title="停止英文标题生成" aria-label="停止英文标题生成" onClick={() => titleOptionAbortRef.current?.abort()}><Square size={15} /></button> : null}{(automationResult?.incompleteCount || detail.coverage.needsSupplementCount) ? <button className="button secondary" disabled={busy === "download-incomplete"} onClick={() => void downloadIncomplete()}><Download size={16} />导出未写入 SKU</button> : null}{detail.retryableFailures.candidateCount ? <button className="button secondary" disabled={!["REVIEWING", "AUTOMATED"].includes(detail.batch.stage) || !data?.writeEnabled || Boolean(busy)} onClick={() => void retryFixableFailures()}><RefreshCw size={16} />重跑技术失败项（{detail.retryableFailures.candidateCount.toLocaleString()}）</button> : null}<button className="button secondary" disabled={!["REVIEWING", "AUTOMATED", "PUBLISHING"].includes(detail.batch.stage) || !data?.writeEnabled || detail.batch.submittedCount === detail.batch.totalCount || Boolean(busy)} onClick={() => void automateBatch()}><UploadCloud size={16} />提交尚未写入项</button></div>{titleOptionProgress ? <div className="ai-title-options-progress" role="status"><div><strong>英文标题 {titleOptionPercent}%</strong><span>{titleOptionProgress.message}</span></div><div className="ai-title-options-count"><span>{titleOptionProgress.completed.toLocaleString()} / {titleOptionProgress.total.toLocaleString()}</span>{titleOptionProgress.failed ? <span className="risk">失败 {titleOptionProgress.failed.toLocaleString()}</span> : null}</div><progress max="100" value={titleOptionPercent} /></div> : null}</details>
        </section> : <section className="ai-result-panel ai-result-empty"><ListChecks size={22} /><strong>暂无批次数据</strong></section>}
      </div>
    </div> : <div className="ai-single-mode" role="tabpanel">
      <div className="ai-single-toolbar"><div><h2>单条改写</h2>{selected ? <span>{selected.merchantSku || selected.itemId} · {selected.siteCode}</span> : null}</div><button className="button secondary" onClick={() => setWorkbenchMode("BATCH")}><ListChecks size={15} />返回批量记录</button></div>
      {selectedLoading ? <section className="ai-editor-loading" role="status"><LoaderCircle className="spin" size={18} /><span>正在读取商品内容…</span></section> : null}
      {selected && detail && ["REVIEWING", "AUTOMATED", "SUBMITTED"].includes(detail.batch.stage) ? <section className="ai-editor-band">
        <div className="section-heading"><div><h2>商品内容</h2><span>{selected.merchantSku || selected.itemId} · {selected.siteCode}</span></div><StatusBadge status={selected.status} /></div>
        <div className="ai-editor-grid"><div className="ai-source-pane"><div className="ai-pane-heading"><strong>原始资料</strong><span>{sourceLookupLabels[selected.sourceLookupStatus]}</span></div>{selected.sourceMessage ? <div className="ai-source-note"><AlertTriangle size={15} />{selected.sourceMessage}</div> : null}{titleRequired ? <div className="ai-content-field"><label>标题 · {titleSourceLabels[selected.sourceTitleOrigin]}</label><p>{selected.sourceTitleEn || "--"}</p></div> : null}{descriptionRequired ? <div className="ai-content-field"><label>描述 · {descriptionSourceLabels[selected.sourceDescriptionOrigin]}</label><pre>{selected.sourceDescription || "--"}</pre></div> : null}</div><div className="ai-review-pane"><div className="ai-pane-heading"><strong>{selected.language === "PT-BR" ? "葡语结果" : "西语结果"}</strong><span>手动修正</span></div>{titleRequired ? <div className="field-group ai-edit-field"><div className="ai-field-label"><label htmlFor="ai-review-title">标题</label><span className={titleCount > selected.titleMaxChars ? "over" : ""}>{titleCount} / {selected.titleMaxChars}</span></div><textarea id="ai-review-title" className="ai-title-input" rows={3} value={reviewTitle} disabled={selected.status === "SUBMITTED"} onChange={(event) => setReviewTitle(event.target.value)} /></div> : null}{descriptionRequired ? <div className="field-group ai-edit-field"><div className="ai-field-label"><label htmlFor="ai-review-description">描述</label><span className={descriptionCount > selected.descriptionMaxChars ? "over" : ""}>{descriptionCount.toLocaleString()} / {selected.descriptionMaxChars.toLocaleString()}</span></div><textarea id="ai-review-description" className="ai-description-input" rows={14} value={reviewDescription} disabled={selected.status === "SUBMITTED"} onChange={(event) => setReviewDescription(event.target.value)} /></div> : null}</div></div>
        {selected.errorMessage ? <div className="form-error" role="alert"><AlertTriangle size={17} />{selected.errorMessage}</div> : null}
        <div className="ai-editor-actions"><button className="button secondary" disabled={selected.status === "SUBMITTED" || busy.startsWith("regenerate:")} onClick={() => void regenerate()}><RefreshCw size={16} />重新生成</button><span /><button className="button primary" disabled={selected.status === "SUBMITTED" || reviewBlocked || busy.startsWith("candidate:")} onClick={() => void updateCandidate()}><Save size={16} />保存修正</button></div>
      </section> : !selectedLoading ? <section className="ai-single-empty"><ListChecks size={24} /><strong>未选择商品</strong><button className="button secondary" onClick={() => setWorkbenchMode("BATCH")}>选择批次商品</button></section> : null}
    </div>}
  </div>;
}
